/**
 * Offline Sync Manager
 * Queues form submissions when offline and syncs when back online
 */

const DB_NAME = "gamify-offline-sync";
const DB_VERSION = 1;
const STORE_NAME = "pending-submissions";

interface PendingSubmission {
  id: string;
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string;
  timestamp: number;
  retryCount: number;
}

// Open IndexedDB connection
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("timestamp", "timestamp", { unique: false });
      }
    };
  });
}

// Generate unique ID
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Queue a submission for later sync
export async function queueSubmission(
  url: string,
  options: RequestInit
): Promise<string> {
  const db = await openDB();
  const id = generateId();

  const submission: PendingSubmission = {
    id,
    url,
    method: options.method || "POST",
    headers: Object.fromEntries(
      options.headers instanceof Headers
        ? options.headers.entries()
        : Object.entries(options.headers || {})
    ),
    body: typeof options.body === "string" ? options.body : JSON.stringify(options.body),
    timestamp: Date.now(),
    retryCount: 0,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.add(submission);

    request.onsuccess = () => {
      console.log(`[OfflineSync] Queued submission: ${id}`);
      resolve(id);
    };
    request.onerror = () => reject(request.error);
  });
}

// Get all pending submissions
export async function getPendingSubmissions(): Promise<PendingSubmission[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Remove a submission after successful sync
export async function removeSubmission(id: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => {
      console.log(`[OfflineSync] Removed submission: ${id}`);
      resolve();
    };
    request.onerror = () => reject(request.error);
  });
}

// Update retry count for a submission
export async function updateRetryCount(id: string, retryCount: number): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const submission = getRequest.result;
      if (submission) {
        submission.retryCount = retryCount;
        store.put(submission);
      }
      resolve();
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

// Sync a single submission
async function syncSubmission(submission: PendingSubmission): Promise<boolean> {
  try {
    const response = await fetch(submission.url, {
      method: submission.method,
      headers: submission.headers,
      body: submission.body,
    });

    if (response.ok) {
      await removeSubmission(submission.id);
      console.log(`[OfflineSync] Successfully synced: ${submission.id}`);
      return true;
    } else if (response.status >= 400 && response.status < 500) {
      // Client error - don't retry
      await removeSubmission(submission.id);
      console.warn(`[OfflineSync] Client error, removed: ${submission.id}`);
      return false;
    } else {
      // Server error - update retry count
      await updateRetryCount(submission.id, submission.retryCount + 1);
      return false;
    }
  } catch (error) {
    console.error(`[OfflineSync] Sync failed for ${submission.id}:`, error);
    await updateRetryCount(submission.id, submission.retryCount + 1);
    return false;
  }
}

// Sync all pending submissions
export async function syncAllPending(): Promise<{ synced: number; failed: number }> {
  const submissions = await getPendingSubmissions();
  let synced = 0;
  let failed = 0;

  console.log(`[OfflineSync] Syncing ${submissions.length} pending submissions...`);

  for (const submission of submissions) {
    // Skip if too many retries (max 5)
    if (submission.retryCount >= 5) {
      await removeSubmission(submission.id);
      console.warn(`[OfflineSync] Max retries reached, removed: ${submission.id}`);
      failed++;
      continue;
    }

    const success = await syncSubmission(submission);
    if (success) {
      synced++;
    } else {
      failed++;
    }
  }

  console.log(`[OfflineSync] Sync complete: ${synced} synced, ${failed} failed`);
  return { synced, failed };
}

// Get count of pending submissions
export async function getPendingCount(): Promise<number> {
  const submissions = await getPendingSubmissions();
  return submissions.length;
}

// Clear all pending submissions
export async function clearAllPending(): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onsuccess = () => {
      console.log("[OfflineSync] Cleared all pending submissions");
      resolve();
    };
    request.onerror = () => reject(request.error);
  });
}
